"use client"

import { useCallback, useState } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ShopifyPeriodSelector } from "@/components/shopify/attribution-dashboard/shopify-period-selector"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useEconomicsSettings } from "@/hooks/use-tenant-settings"
import { ConfigPerformanceView } from "./config-performance-view"
import { BundlePerformanceView } from "./bundle-performance-view"
import { BundleMatrixView } from "./bundle-matrix-view"
import { EconomicsParamsCard } from "./economics-params-card"
import type { ShopifyPeriod } from "@/components/shopify/attribution-dashboard/period-constants"
import type { EconomicsTab } from "./types"

const VALID_TABS: EconomicsTab[] = ["config", "matrix", "bundle"]

interface EconomicsContentProps {
  integrationId: string
  dateRange: { month_start_utc: Date; month_end_utc: Date; timezone: string }
  period: ShopifyPeriod
  customRange?: { from: string; to: string } | null
  onPeriodChange: (period: ShopifyPeriod, customRange?: { from: string; to: string }) => void
  minimumOrderTotal: number
  stores: { id: string; shop_domain: string }[]
  selectedStore: string
  onStoreChange: (storeId: string) => void
}

export function EconomicsContent({
  integrationId,
  dateRange,
  period,
  customRange,
  onPeriodChange,
  minimumOrderTotal,
  stores,
  selectedStore,
  onStoreChange,
}: EconomicsContentProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const tabParam = searchParams.get("tab")
  const initialTab = VALID_TABS.includes(tabParam as EconomicsTab) ? (tabParam as EconomicsTab) : "config"
  const [activeTab, setActiveTab] = useState<EconomicsTab>(initialTab)

  // Visit-tracking forceMount: once the user opens a tab, its content stays
  // mounted (with the `hidden` HTML attribute when inactive — Radix attaches
  // it automatically). This preserves the tab's component state + data hooks
  // across switches so coming back doesn't remount + replay useEffect + flash
  // a skeleton while the module-cache resolves. Without this, each tab
  // switch felt like a fresh load even when the underlying data was cached.
  //
  // Trade-off: after visiting all tabs, all three live concurrently — heavier
  // on the page, but tab switching is instant. Period-selector changes still
  // invalidate every mounted tab's cache key in one shot, so cross-tab
  // refresh stays correct.
  const [visitedTabs, setVisitedTabs] = useState<ReadonlySet<EconomicsTab>>(
    () => new Set([initialTab]),
  )

  const handleTabChange = useCallback(
    (value: string) => {
      const tab = value as EconomicsTab
      setActiveTab(tab)
      setVisitedTabs((prev) => (prev.has(tab) ? prev : new Set([...prev, tab])))
      const next = new URLSearchParams(searchParams.toString())
      next.set("tab", tab)
      router.replace(`${pathname}?${next.toString()}`, { scroll: false })
    },
    [router, pathname, searchParams],
  )
  const { settings: economicsSettings, updateSettings } = useEconomicsSettings()

  const monthStartUtc = dateRange.month_start_utc.toISOString()
  const monthEndUtc = dateRange.month_end_utc.toISOString()

  return (
    <div className="space-y-4">
      {/* Controls bar */}
      <div className="flex flex-wrap items-center gap-3">
        {stores.length > 1 && (
          <Select value={selectedStore} onValueChange={onStoreChange}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select store" />
            </SelectTrigger>
            <SelectContent>
              {stores.map((store) => (
                <SelectItem key={store.id} value={store.id}>
                  {store.shop_domain}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <ShopifyPeriodSelector
          period={period}
          customRange={customRange}
          onPeriodChange={onPeriodChange}
        />
      </div>

      <EconomicsParamsCard settings={economicsSettings} onUpdate={updateSettings} />

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="config">Product Economics</TabsTrigger>
          <TabsTrigger value="matrix">Bundle Matrix</TabsTrigger>
          <TabsTrigger value="bundle">Bundle Performance</TabsTrigger>
        </TabsList>

        {visitedTabs.has("config") && (
          <TabsContent value="config" forceMount className="mt-4 data-[state=inactive]:hidden">
            <ConfigPerformanceView
              integrationId={integrationId}
              monthStartUtc={monthStartUtc}
              monthEndUtc={monthEndUtc}
              minimumOrderTotal={minimumOrderTotal}
              periodLabel={period}
              timezone={dateRange.timezone}
              economicsSettings={economicsSettings}
              onUpdateSettings={updateSettings}
            />
          </TabsContent>
        )}

        {visitedTabs.has("matrix") && (
          <TabsContent value="matrix" forceMount className="mt-4 data-[state=inactive]:hidden">
            <BundleMatrixView
              integrationId={integrationId}
              monthStartUtc={monthStartUtc}
              monthEndUtc={monthEndUtc}
              minimumOrderTotal={minimumOrderTotal}
              timezone={dateRange.timezone}
              economicsSettings={economicsSettings}
              periodLabel={period}
            />
          </TabsContent>
        )}

        {visitedTabs.has("bundle") && (
          <TabsContent value="bundle" forceMount className="mt-4 data-[state=inactive]:hidden">
            <BundlePerformanceView
              integrationId={integrationId}
              monthStartUtc={monthStartUtc}
              monthEndUtc={monthEndUtc}
              minimumOrderTotal={minimumOrderTotal}
              timezone={dateRange.timezone}
              economicsSettings={economicsSettings}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}
